import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Building2, Sparkles } from "lucide-react";
import VorionLogo from "../components/VorionLogo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1800&q=70";

export default function Landing() {
  return (
    <div
      className="min-h-screen bg-[#0B0B0E] text-white"
      data-testid="landing-page"
    >
      {/* Top bar */}
      <header className="px-6 lg:px-10 py-5 flex items-center justify-between border-b border-zinc-900/80">
        <VorionLogo variant="wordmark" size={36} testid="landing-brand" />
        <nav className="flex items-center gap-2">
          <Link
            to="/login"
            data-testid="landing-login-btn"
            className="text-sm text-zinc-300 hover:text-white px-4 py-2 rounded-md transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="landing-register-btn"
            className="text-sm bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold px-4 py-2 rounded-md transition-colors inline-flex items-center gap-1.5"
          >
            Create workspace <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section
        className="relative px-6 lg:px-16 py-16 lg:py-24 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(120deg, rgba(11,11,14,0.96) 0%, rgba(11,11,14,0.85) 40%, rgba(11,11,14,0.45) 80%, transparent), url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4A968]/40 bg-[#D4A968]/10 text-[10px] font-mono uppercase tracking-widest text-[#D4A968] mb-6">
            <Sparkles className="w-3 h-3" />
            Vorion · Hotel Operations Portal
          </div>
          <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
            Your private{" "}
            <span className="text-[#D4A968]">command room</span>
            <br />
            for every hotel you run.
          </h1>
          <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-xl leading-relaxed">
            Every user gets their own isolated workspace — outlet
            dashboards, demand calendars, gap engines, audits, chat and AI
            recommendations. Nothing leaks between accounts.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              data-testid="landing-cta-register"
              className="bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold px-6 py-3 rounded-md inline-flex items-center gap-2 text-sm transition-colors"
            >
              Create your workspace <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              data-testid="landing-cta-login"
              className="bg-transparent border border-zinc-700 hover:border-[#D4A968] text-zinc-100 px-6 py-3 rounded-md inline-flex items-center gap-2 text-sm transition-colors"
            >
              I already have an account
            </Link>
          </div>
        </div>
      </section>

      {/* Feature trio */}
      <section className="px-6 lg:px-16 py-16 border-t border-zinc-900/80">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl">
          <FeatureCard
            icon={<Building2 className="w-5 h-5 text-[#D4A968]" />}
            title="One workspace per user"
            body="Sign up with an email — your private workspace boots instantly. No shared dashboards, no cross-tenant data."
          />
          <FeatureCard
            icon={<ShieldCheck className="w-5 h-5 text-[#10B981]" />}
            title="Two-tier access"
            body="Workspace owners run their property. Global admins (Vorion HQ) monitor health, members and audit trails — never your data."
          />
          <FeatureCard
            icon={<Sparkles className="w-5 h-5 text-[#38BDF8]" />}
            title="AI demand + gap engine"
            body="The Outlet Demand Calendar and the Annual Gap Opportunity Engine surface revenue at risk and auto-create Kanban tasks."
          />
        </div>
      </section>

      <footer className="px-6 lg:px-16 py-8 border-t border-zinc-900/80 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
        <span>© Vorion · Hotel Operations Portal</span>
        <span>
          New here?{" "}
          <Link
            to="/register"
            className="text-[#D4A968] hover:text-[#E8C285]"
            data-testid="landing-footer-register"
          >
            Register →
          </Link>
        </span>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="bg-[#141417] border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors">
      <div className="w-9 h-9 rounded-md bg-[#0B0B0E] border border-zinc-800 flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="text-base font-semibold text-white mb-1">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}
